import * as D from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { pick, txt } from "@/lib/format";
import { DishImage } from "./DishImage";
import { DietTags, LabelBadges } from "./Badges";
import { Price } from "./Price";
import { LangSwitch } from "@/components/LangSwitch";

export const ItemModal = ({ item, category, open, onClose, lang, s, t, container, onLang }) => {
  if (!item) return null;
  const title = pick(item, "title", lang);
  const desc = pick(item, "description", lang);
  return (
    <D.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <D.Portal container={container}>
        <D.Overlay className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center md:p-6">
          <D.Content
            dir={lang === "ar" ? "rtl" : "ltr"}
            lang={lang}
            aria-describedby={undefined}
            data-testid="item-modal"
            data-item-id={item.id}
            className="pointer-events-auto relative flex h-full w-full flex-col overflow-y-auto overscroll-contain outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.97] md:h-auto md:max-h-[88vh] md:w-[min(1000px,100%)] md:flex-row md:overflow-hidden md:rounded-[calc(var(--wt-radius)+8px)] md:shadow-2xl"
            style={{ background: "var(--wt-surface)", color: "var(--wt-text)", fontFamily: "var(--wt-font)" }}
          >
            <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden md:aspect-auto md:min-h-[480px] md:w-[56%]" style={{ background: "var(--wt-card)" }}>
              <DishImage url={item.image_url} alt={title.text} sizes="(min-width:768px) 560px, 100vw" size="lg" priority />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent md:hidden" />
            </div>
            <div className="flex flex-1 flex-col gap-4 p-6 pb-10 md:overflow-y-auto md:p-8">
              {category && <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "var(--wt-primary)" }}>{txt(category, "name", lang)}</span>}
              <D.Title lang={title.lang} dir={title.fallback ? "auto" : undefined} data-testid="item-modal-title" className="break-words text-2xl font-black leading-tight sm:text-3xl">
                {title.text}
              </D.Title>
              <LabelBadges labels={item.labels} t={t} />
              <Price item={item} s={s} lang={lang} large testid="item-modal-price" />
              {!item.is_available && (
                <div data-testid="item-modal-unavailable" className="rounded-xl px-3 py-2 text-sm font-semibold" style={{ background: "color-mix(in srgb, var(--wt-text) 8%, transparent)" }}>
                  {t("pub.unavailableLong")}
                </div>
              )}
              {desc.text && (
                <p lang={desc.lang} dir={desc.fallback ? "auto" : undefined} data-testid="item-modal-description" className="whitespace-pre-line break-words text-base leading-relaxed text-[var(--wt-muted)]">
                  {desc.text}
                </p>
              )}
              <DietTags tags={item.dietary_tags} t={t} />
            </div>
            <div className="absolute start-3 top-3 z-10 rounded-full bg-black/60 text-white backdrop-blur-md" style={{ "--ls-active": "var(--wt-primary)" }}>
              <LangSwitch lang={lang} onChange={onLang} testid="modal-lang-switch" className="border-white/10" />
            </div>
            <D.Close
              data-testid="item-modal-close"
              aria-label={t("pub.close")}
              className="absolute end-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-black/60 text-white backdrop-blur-md transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-5 w-5" />
            </D.Close>
          </D.Content>
        </div>
      </D.Portal>
    </D.Root>
  );
};
